import { AdaptiveResolutionCanvas } from "@/components/r3f/canvas/adaptive-resolution-canvas";
import { useClippingPlanes } from "@/hooks/use-clipping-planes";
import { useDetectTooManyRedraws } from "@/hooks/use-detect-too-many-redraws";
import { useCached3DObjectsIfReady } from "@/object-cache";
import { computeCombinedPointCloudBoundingBox } from "@/registration-tools/utils/camera-views";
import { useAppSelector } from "@/store/store-hooks";
import { CanvasOverlay } from "@faro-lotv/app-component-toolbox";
import { CircularProgress, useToast } from "@faro-lotv/flat-ui";
import { RegistrationState } from "@faro-lotv/service-wires";
import { Box } from "@mui/material";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Box3, Plane } from "three";
import { useDataPreparationOverlayElements } from "../data-preparation-overlay-context";
import { useLoadRevisionPointCloudStreams } from "../loading/use-load-revision-point-cloud-streams";
import {
  selectPointCloudStreamIdsForHoveredEntities,
  selectPointCloudStreamIdsForSelectedEntities,
  selectValidRevisionScans,
} from "../store/data-preparation-ui/data-preparation-ui-selectors";
import { Projection } from "../ui/projection-switch";
import { CanvasScanMarkers } from "./canvas-scan-markers";
import { RenderingStatsCollector } from "./debug/rendering-stats-collector";
import { OVERLAY_Z_INDEX } from "./registration-connections";
import { RegistrationConnectionsInstanced } from "./registration-connections-instanced";
import {
  invalidateSceneCheapEvent,
  RevisionRenderingPipeline,
} from "./revision-rendering-pipeline";
import { RevisionScansControls } from "./revision-scans-controls";
import { RevisionScansRenderer } from "./revision-scans-renderer";
import { RevisionScansTools } from "./revision-scans-tools";

type RevisionScansProps = {
  /** The registration states of the scans, used to display connections between scans. */
  registrationState?: RegistrationState;

  /** A UI overlay to display on top of the canvas. */
  overlay?: React.ReactNode;

  /** Whether the user can move and rotate the scans. */
  isEditingScans: boolean;

  /** Whether registration editing is enabled (edit mode) */
  isEditRegistrationEnabled: boolean;
};

/** @returns A 3D scene displaying the scans included in the revision. */
export const RevisionScansScene = memo(function RevisionScansScene({
  registrationState,
  overlay,
  isEditingScans,
  isEditRegistrationEnabled,
}: RevisionScansProps): JSX.Element | null {
  useDetectTooManyRedraws("RevisionScansScene");

  const canvas = useRef<HTMLCanvasElement>(null);
  const scanEntities = useAppSelector(selectValidRevisionScans);

  const pointCloudStreams = useLoadRevisionPointCloudStreams(scanEntities);
  const pointCloudObjects = useCached3DObjectsIfReady(
    pointCloudStreams,
    "RevisionScansScene",
  );

  const hoveredPointCloudIds = useAppSelector(
    selectPointCloudStreamIdsForHoveredEntities,
  );

  const selectedPointCloudIds = useAppSelector(
    selectPointCloudStreamIdsForSelectedEntities,
  );

  const { projection, cameraOptions3D, centerCameraEvent, activeTool } =
    useDataPreparationOverlayElements();

  const revisionScanIds = useMemo(
    () => scanEntities.map((scan) => scan.id),
    [scanEntities],
  );

  const [combinedPcBoundingBox, setCombinedPcBoundingBox] = useState<Box3>();

  const { clippingPlanes, setClippingPlanes } = useClippingPlanes();

  // --- Toast logic for edit mode and projection ---
  const { openToast } = useToast();
  const [isRegEditToastShown, setRegEditToastShown] = useState(false);

  useEffect(() => {
    if (
      !isRegEditToastShown &&
      isEditRegistrationEnabled &&
      projection !== Projection.twoDimensional
    ) {
      openToast({
        title: "Edit registration disabled",
        message: "The registration can only be edited in the 2D overview.",
        variant: "warning",
      });
      setRegEditToastShown(true);
    }
    if (
      projection === Projection.twoDimensional ||
      !isEditRegistrationEnabled
    ) {
      setRegEditToastShown(false);
    }
  }, [projection, isEditRegistrationEnabled, isRegEditToastShown, openToast]);

  // Apply the updated clipping planes to hide the points outside
  const updateClippingPlanes = useCallback(
    (clippingPlanes: Plane[]) => {
      setClippingPlanes(clippingPlanes);
      // Refresh the scene to hide the points outside of the clipping box
      invalidateSceneCheapEvent.emit();
    },
    [setClippingPlanes],
  );

  // Display a loading spinner if the point cloud streams are still loading
  if (
    !pointCloudStreams ||
    pointCloudObjects.length !== pointCloudStreams.length
  ) {
    return (
      <Box
        component="div"
        sx={{
          display: "flex",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          flexGrow: 1,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        position: "relative",
        "& div": {
          zIndex: 0,
        },
      }}
    >
      <AdaptiveResolutionCanvas
        style={{ position: "absolute" }}
        orthographic
        ref={canvas}
      >
        <RevisionScansRenderer
          scanEntities={scanEntities}
          pointCloudObjects={pointCloudObjects}
          hoveredPointCloudIds={hoveredPointCloudIds}
          selectedPointCloudIds={selectedPointCloudIds}
          onInitialPositioning={() => {
            // The world space bounding box can only be computed after the point clouds have been positioned
            setCombinedPcBoundingBox(
              computeCombinedPointCloudBoundingBox(
                pointCloudObjects,
                new Box3(),
              ),
            );
          }}
          clippingPlanes={clippingPlanes}
        />
        <RevisionScansControls
          centerCameraEvent={centerCameraEvent}
          revisionScanIds={revisionScanIds}
          projection={projection}
          cameraOptions3D={cameraOptions3D}
          isEditingScans={isEditingScans}
        />

        <RegistrationConnectionsInstanced
          revisionState={registrationState}
          clippingPlanes={clippingPlanes}
        />

        <CanvasScanMarkers
          scanEntities={scanEntities}
          clippingPlanes={clippingPlanes}
        />
        <RevisionScansTools
          activeTool={activeTool}
          combinedPcBoundingBox={combinedPcBoundingBox}
          onClippingPlanesChanged={updateClippingPlanes}
        />

        <RevisionRenderingPipeline pointCloudObjects={pointCloudObjects} />

        <RenderingStatsCollector
          scanEntities={scanEntities}
          pointCloudObjects={pointCloudObjects}
        />
      </AdaptiveResolutionCanvas>

      <CanvasOverlay
        canvas={canvas}
        sx={{
          // Fix 3d HTML elements overlapping other UI
          "& div": {
            zIndex: OVERLAY_Z_INDEX,
          },
        }}
      >
        {overlay}
      </CanvasOverlay>
    </Box>
  );
});
